/**
 * ARTWORK STUDIO — the Canva source adapter (no React).
 *
 * The client NEVER talks to Canva (hard rule #2). Everything is brokered by the
 * JWT backend, which holds the OAuth refresh token (`folder:read`,
 * `design:meta:read`).
 *
 * THE LIVE ROUTE IS `GET /api/canva/items` and it takes `folderId` — plus `q`
 * for a search that the backend walks over THAT FOLDER'S SUBTREE ONLY
 * (`canva.js#searchInFolder`), never the whole account. That is the
 * folder-scoped guarantee from Master Spec Section 2, and it is enforced
 * server-side, not by this file.
 *
 * Response shape from that route: `{ ok, items: [...], continuation }`, where a
 * folder entry carries `isFolder: true` + `name` and a design carries `title` +
 * `thumb`. This module splits them into `folders` / `items` so the navigator
 * renders a tree.
 *
 * THREE SOURCES, in order, and the UI is told which one it got:
 *   1. `live`    — the real connected Canva account
 *   2. `manual`  — Canva unreachable/not connected: the picker still works via
 *                  the manual asset-ref field and the upload/attach fallback
 *   3. `demo`    — `?demo=1` only, and BADGED, so canned designs are never
 *                  mistaken for the real library
 */
import { api } from '../../../lib/api.js'
import { STUDIO_FOLDERS, CANVA_ROOT, FOLDER_NAMES } from './folders.js'
import { collectFolder, PAGE_CAP } from './canvaPaging.js'
import { collectTree, filterTree, TREE_FOLDER_CAP, TREE_ITEM_CAP } from './canvaTree.js'
import { isFolderEntry, entryId } from './canvaEntry.js'

export { PAGE_CAP, filterTree, TREE_FOLDER_CAP, TREE_ITEM_CAP }

export const CANVA_SOURCE = { LIVE: 'live', MANUAL: 'manual', DEMO: 'demo' }

export function isDemo() {
  if (typeof window === 'undefined') return false
  try {
    return new URLSearchParams(window.location.search).get('demo') === '1'
  } catch {
    return false
  }
}

/* ------------------------------------------------------------------ *
 * Normalizer — Canva's list shape -> the navigator's tree node
 * ------------------------------------------------------------------ */

function nodeFrom(res, folderId) {
  if (!res || typeof res !== 'object') return null
  const raw = Array.isArray(res.items) ? res.items : Array.isArray(res.results) ? res.results : null
  if (!raw) return null
  const folders = []
  const items = []
  for (const x of raw) {
    if (!x) continue
    // The SAME classifier the engine navigator uses (canvaEntry.js). These two
    // normalizers used to decide folder-vs-design separately and drifted: this
    // one read the backend's `isFolder` flag, the engine one did not, so the
    // Composer and Elements tabs rendered sub-folders as undrillable tiles while
    // the Builder rendered them correctly. One function now answers for both.
    const nested = x.folder || x.design || x
    if (isFolderEntry(x)) {
      const id = entryId(x)
      if (id) folders.push({ id, name: nested.name || nested.title || 'Folder' })
    } else {
      const id = entryId(x)
      if (!id) continue
      items.push({
        id,
        name: nested.title || nested.name || 'Untitled',
        // `thumbnail` is an OBJECT in Canva's raw shape and a string in older
        // payloads — only ever hand a string to <img src>.
        thumb: nested.thumb || nested.thumbnail?.url ||
          (typeof nested.thumbnail === 'string' ? nested.thumbnail : null) || null,
        url: nested.view || nested.urls?.view_url || nested.url || null,
        source: 'Canva',
      })
    }
  }
  return {
    id: folderId,
    name: FOLDER_NAMES[folderId] || res.name || 'Folder',
    folders,
    items,
    continuation: res.continuation || null,
    source: CANVA_SOURCE.LIVE,
  }
}

/* ------------------------------------------------------------------ *
 * The canned tree — ?demo=1 ONLY, and always badged
 * ------------------------------------------------------------------ */

function swatch(hex, glyph = '') {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="${hex}"/><circle cx="50" cy="50" r="30" fill="#fff" opacity="0.2"/><text x="50" y="60" font-family="system-ui,sans-serif" font-size="30" font-weight="700" fill="#fff" text-anchor="middle" opacity="0.9">${glyph}</text></svg>`
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg)
}

const D = (id, name, thumb) => ({ id, name, thumb, url: null, source: 'demo' })

const DEMO_TREE = {
  [CANVA_ROOT.id]: {
    name: 'SR Artwork Engine',
    folders: [
      { id: STUDIO_FOLDERS.bezel.id, name: 'Bezel & Top Designs' },
      { id: STUDIO_FOLDERS.shanks.id, name: 'Side Shanks' },
      { id: STUDIO_FOLDERS.housings.id, name: 'Ring Housings & Templates' },
      { id: STUDIO_FOLDERS.backgrounds.id, name: 'Presentation Templates' },
    ],
    items: [],
  },
  [STUDIO_FOLDERS.bezel.id]: {
    name: 'Bezel & Top Designs',
    folders: [{ id: 'demo-bezel-fb', name: 'Football' }, { id: 'demo-bezel-bsb', name: 'Baseball' }],
    items: [D('bz-cushion', 'Cushioned Square Championship', swatch('#c7a24a', '◆'))],
  },
  'demo-bezel-fb': {
    name: 'Football',
    folders: [],
    // A BIG folder on purpose. The real Bezel & Top holds 300+ designs, which is
    // what exposed the single-page bug — so ?demo=1 must exercise the paging
    // loop rather than fitting inside one page and proving nothing.
    items: [
      D('bz-fb-1', 'Football Double-Halo Top', swatch('#8a5a2b', '★')),
      D('bz-fb-2', 'Football Sunburst Face', swatch('#123c69', '✦')),
      ...Array.from({ length: 44 }, (_, i) =>
        D(`bz-fb-${i + 3}`, `Football Bezel Study ${String(i + 3).padStart(2, '0')}`, swatch(i % 2 ? '#8a5a2b' : '#123c69', '◆'))),
    ],
  },
  'demo-bezel-bsb': {
    name: 'Baseball',
    folders: [],
    items: [D('bz-bsb-1', 'Baseball Pavé Crest Top', swatch('#0e7c66', '◆'))],
  },
  [STUDIO_FOLDERS.shanks.id]: {
    name: 'Side Shanks',
    folders: [{ id: 'demo-shank-fb', name: 'Football' }, { id: 'demo-shank-bsb', name: 'Baseball' }],
    items: [],
  },
  'demo-shank-fb': {
    name: 'Football',
    folders: [],
    items: [D('sh-fb-1', 'Chevron Lockup Shank', swatch('#6b0f1a', '▲')), D('sh-fb-2', 'Sculpted Mascot Shank', swatch('#274060', '★'))],
  },
  'demo-shank-bsb': {
    name: 'Baseball',
    folders: [],
    items: [D('sh-bsb-1', 'Year & Record Shank', swatch('#1d3557', '26'))],
  },
  [STUDIO_FOLDERS.housings.id]: {
    name: 'Ring Housings & Templates',
    folders: [],
    items: [D('h-classic', 'Classic Championship Housing', swatch('#4a4a4a', '○'))],
  },
  [STUDIO_FOLDERS.backgrounds.id]: {
    name: 'Presentation Templates',
    folders: [],
    items: [D('bg-brand', 'Brand Template — color slots', swatch('#101820', '▦')), D('bg-story', 'Design Story – Fall Edition', swatch('#7a1220', '▦'))],
  },
}

/** The canned feed pages exactly like the real one, so demo proves the loop. */
const DEMO_PAGE = 12

function demoNode(folderId, q, continuation) {
  const base = DEMO_TREE[folderId] || DEMO_TREE[CANVA_ROOT.id]
  const query = String(q || '').toLowerCase().trim()
  if (!query) {
    const from = Number(continuation) || 0
    const slice = base.items.slice(from, from + DEMO_PAGE)
    const next = from + DEMO_PAGE < base.items.length ? String(from + DEMO_PAGE) : null
    return {
      id: folderId,
      name: base.name,
      // Subfolders come with the first page only — the same as the live route,
      // which lists them once and then pages the designs.
      folders: from === 0 ? base.folders : [],
      items: slice,
      continuation: next,
      source: CANVA_SOURCE.DEMO,
    }
  }
  // Scoped search: this folder's subtree only, exactly like the live route.
  const out = []
  const seen = new Set()
  const walk = (id) => {
    if (seen.has(id)) return
    seen.add(id)
    const n = DEMO_TREE[id]
    if (!n) return
    for (const it of n.items) if (it.name.toLowerCase().includes(query)) out.push(it)
    for (const f of n.folders) walk(f.id)
  }
  walk(folderId)
  return { id: folderId, name: base.name, folders: [], items: out, continuation: null, source: CANVA_SOURCE.DEMO }
}

/* ------------------------------------------------------------------ *
 * Public
 * ------------------------------------------------------------------ */

/** Is the live feed configured + connected? `{ configured, connected }`. */
export async function canvaStatus() {
  if (isDemo()) return { configured: true, connected: true, demo: true }
  try {
    const res = await api.canvaStatus()
    return { configured: !!res?.configured, connected: !!res?.connected, demo: false }
  } catch (err) {
    // The probe FAILED — we did not learn that Canva is unconfigured, we
    // learned nothing. `unreachable` keeps those two apart so `connectionGap`
    // does not blame a Railway variable for an expired session.
    return { configured: false, connected: false, demo: false, unreachable: true, status: err?.status || null }
  }
}

/* ------------------------------------------------------------------ *
 * THE CONNECTION — asked ONCE for the whole studio
 *
 * Thirteen steps mount up to six pickers. Each one asking `/api/canva/status`
 * on open is six identical round-trips and six chances to render a different
 * answer. One shared in-flight promise means every picker, and the banner at
 * the top of the page, are looking at the same fact.
 * ------------------------------------------------------------------ */

let _statusPromise = null

/** The shared status, resolved once per page load. */
export function sharedCanvaStatus() {
  if (!_statusPromise) _statusPromise = canvaStatus()
  return _statusPromise
}

/** Re-ask after a connect attempt — the only thing that may change the answer. */
export function refreshCanvaStatus() {
  _statusPromise = canvaStatus()
  return _statusPromise
}

// The gap model is PURE and lives on its own (no api.js, so no import.meta)
// — that is what lets verify-studio.mjs assert its wording directly.
export { connectionGap } from './canvaGap.js'

/** Start the one-click OAuth. Returns the consent URL, or throws. */
export async function canvaConnectUrl() {
  const res = await api.canvaConnectUrl()
  const url = res?.url
  if (!url) throw new Error('The backend did not return a Canva authorization URL.')
  return url
}

/**
 * Browse ONE folder, or search WITHIN it.
 *
 * @param {string} folderId  the folder to open (never "all of Canva")
 * @param {{ q?: string, continuation?: string, signal?: AbortSignal }} opts
 * @returns {Promise<{id,name,folders,items,continuation,source,reason?}>}
 */
export async function browseFolder(folderId = CANVA_ROOT.id, opts = {}) {
  const id = folderId || CANVA_ROOT.id
  const { q = '', continuation = null, signal } = opts

  if (isDemo()) return demoNode(id, q, continuation)

  try {
    // `folderId` and `q` are the route's real parameter names — the scoped
    // subtree search happens server-side (canva.js#searchInFolder).
    const res = await api.canvaItems(
      { folderId: id, q: q || undefined, continuation: continuation || undefined },
      { signal }
    )
    const node = nodeFrom(res, id)
    if (node) return node
    return { id, name: FOLDER_NAMES[id] || 'Folder', folders: [], items: [], continuation: null, source: CANVA_SOURCE.MANUAL, reason: 'The Canva feed answered with no items for this folder.' }
  } catch (err) {
    if (err?.name === 'AbortError') throw err
    const detail = err?.status === 503
      ? 'The live Canva feed is not connected on the backend yet.'
      : `The Canva feed is unavailable (${err?.status || 'network error'}).`
    return { id, name: FOLDER_NAMES[id] || 'Folder', folders: [], items: [], continuation: null, source: CANVA_SOURCE.MANUAL, reason: detail }
  }
}

/**
 * Browse a folder AND FOLLOW ITS CONTINUATION TOKEN TO EXHAUSTION.
 *
 * `browseFolder` is one page. Bezel & Top Designs holds 300+ designs and the
 * route answers a page at a time, so one page is a fraction of the folder with
 * nothing on screen to say the rest exists. This is the call every picker
 * makes: it keeps requesting with the token the feed hands back until the token
 * is empty, and reports each page through `onPage` so the grid fills as the
 * folder loads instead of waiting on all of it.
 *
 * Bounded by `PAGE_CAP`. If a folder is deeper than the cap the leftover token
 * comes back on `continuation` so the UI offers "Load more" — the loop never
 * truncates silently.
 *
 * @param {string} folderId
 * @param {{ q?: string, signal?: AbortSignal, onPage?: (node) => void,
 *           continuation?: string|null, maxPages?: number, seed?: object|null }} opts
 */
export async function browseFolderAll(folderId = CANVA_ROOT.id, opts = {}) {
  const id = folderId || CANVA_ROOT.id
  const { q = '', signal, onPage, continuation = null, maxPages = PAGE_CAP, seed = null } = opts
  return collectFolder(
    (cont) => browseFolder(id, { q, continuation: cont, signal }),
    { start: continuation, maxPages, onPage, seed }
  )
}

/**
 * Browse a folder AND EVERY FOLDER UNDER IT — the call the element pickers make.
 *
 * `browseFolderAll` is one folder walked to the end of its pages. That is depth,
 * and it was already right. This is BREADTH: "Bezel & Top Designs" keeps a dozen
 * designs at its top level and the other 300+ in sport sub-folders, so a picker
 * that only ever asked for the opened folder showed a dozen tiles and a row of
 * folders — correct, and nowhere near complete. This walks the whole subtree,
 * stamps every design with the folder it came from, and reports progress as it
 * goes so the grid fills instead of blocking on the last folder.
 *
 * Sub-folders are still returned on `folders`, so drill-down and the breadcrumb
 * keep working exactly as before underneath the flattened view.
 *
 * @param {string} folderId
 * @param {{ signal?: AbortSignal, onProgress?: (node) => void,
 *           maxFolders?: number, maxItems?: number }} opts
 */
export async function browseFolderDeep(folderId = CANVA_ROOT.id, opts = {}) {
  const id = folderId || CANVA_ROOT.id
  const { signal, onProgress, maxFolders, maxItems } = opts
  return collectTree(
    (fid, o) => browseFolderAll(fid, { signal, onPage: o?.onPage }),
    { id, name: FOLDER_NAMES[id] || 'Folder' },
    { signal, onProgress, maxFolders, maxItems }
  )
}
